import React, { useState } from 'react';
import { Checkbox, FormControlLabel, FormGroup } from '@mui/material';
import FileUploader from 'src/app/common/components/FileUploader';
import { createBlob, getBlob } from 'src/app/common/network';
import { fileUpload, toAbsoluteUrl } from 'src/app/common/utils';
import { useDispatch } from 'react-redux';
import { Translation } from 'src/app/i18n';
import { AgentSegment } from '../../../types/prospect-referral-types';

interface AgentSegmentOption {
  key: string;
  label: string;
  value: boolean;
  isRequired?: boolean;
  isUpload?: boolean;
}

interface AgentSegmentFieldProps {
  value?: AgentSegment;
  onChange?: (value: AgentSegment) => void;
  options: AgentSegmentOption[];
  disabled: boolean;
}

const parseCSVUserList = (file: File): Promise<string[]> => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => {
      let csvData = String(reader.result);
      csvData = csvData.replace(/\ufeff/g, '').replace(/\r/g, '');
      const rows = csvData.split('\n').filter((row) => row.trim() !== '');
      const userList = rows.slice(1).map((row) => row.split(',')[0].trim()).filter(Boolean);
      resolve(userList);
    };
    reader.readAsBinaryString(file);
  });
};

const AgentSegmentField: React.FC<AgentSegmentFieldProps> = (props) => {
  const [selectedValue, setSelectedValue] = useState<AgentSegment>(props.value as AgentSegment);
  const dispatch = useDispatch();

  const handleChange = (key: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setSelectedValue((prev: any) => {
      const next: any = { ...prev, [key]: checked };
      if (key === 'isGroupOfUsers' && !checked) {
        next.userFile = null;
        next.userList = [];
      }
      props.onChange?.(next);
      return next;
    });
  };

  const handleUploadFile = async ({
    file,
    fileName,
    fileType,
  }: {
    file: any;
    fileName: string;
    fileType: string;
  }) => {
    const userList = await parseCSVUserList(file as File);
    const createBlobRes = await createBlob({ mimeType: fileType, accessLevel: 'anonymous' }, dispatch);
    await fileUpload(createBlobRes.url, file, () => {});
    const blobDetail = await getBlob({ resourceIds: createBlobRes.blobId }, dispatch);
    const result = blobDetail[0];
    if (result) {
      return {
        id: result.blobId,
        fileName,
        url: result.url,
        type: fileType,
        userList,
      };
    }
  };

  const onChangeFileUploader = (item: any) => {
    setSelectedValue((prev: any) => {
      const next = {
        ...prev,
        userFile: item ? { id: item.id, fileName: item.fileName, url: item.url, type: item.type } : null,
        userList: item?.userList || [],
      };
      props.onChange?.(next);
      return next;
    });
    return item ? item : undefined;
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'row', position: 'relative', width: '100%', marginBottom: 10 }}>
      <FormGroup style={{ display: 'flex', flexDirection: 'row' }}>
        {props.options.map((item) => (
          <React.Fragment key={item.key}>
            <FormControlLabel
              control={
                <Checkbox
                  disabled={props.disabled || item.isRequired}
                  checked={item.isRequired || selectedValue?.[item.key as keyof AgentSegment] as boolean}
                  onChange={handleChange(item.key)}
                  name={item.label}
                />
              }
              label={item.label}
            />
          </React.Fragment>
        ))}
        {props.options.map((item) => (
          <React.Fragment key={item.key}>
            {item.isUpload && selectedValue?.isGroupOfUsers && (
              <>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <FileUploader
                    disabled={props.disabled}
                    upload={handleUploadFile}
                    value={props.value?.userFile as any}
                    onChange={onChangeFileUploader}
                    showAllowText={'Upload the excel file list of agent (max =10MB)'}
                    maxFileSize={10}
                    allowedFileTypes={['csv', 'xlsx', 'xls']}
                  />
                </div>
                {!props.value?.userFile && (
                  <div style={{ flexBasis: '100%', height: 'auto' }}>
                    <a href={toAbsoluteUrl('/templates/Template_Group_Of_User.csv')}>
                      {Translation('recruitment.form.downloadTemplate')}
                    </a>
                  </div>
                )}
              </>
            )}
          </React.Fragment>
        ))}
      </FormGroup>
    </div>
  );
};

export default AgentSegmentField;
